/**
 * orderMirrorMiddleware — keeps the order_mirror store in lock-step with the
 * Redux orders slice.
 *
 * One hook point for EVERY order mutation: server fetches (setAll), socket
 * broadcasts (upserts), local creates/edits (thunks), status changes, sync
 * remaps. After any orders/* action, changed entities are diffed by reference
 * and mirrored in one batch — so the mirror always holds the freshest copy of
 * the business day without touching any individual flow.
 *
 * What it deliberately does NOT mirror:
 *  - drafts (carts still being composed — never real orders)
 *  - History-tab fetches (orders/fetchOrderHistory*) — those pull PAST days
 *    into Redux; mirroring them would re-stamp old orders with the current
 *    business date (orderMirror.toRow has a second age guard as well)
 *  - clearOrders / removeOrderById removals — removal from Redux is a VIEW
 *    change (branch switch, refresh), not an order event. Real deletions
 *    arrive as status changes or deleteOrder actions.
 */

import orderMirror from '../../services/orderMirror';

const SKIP_PREFIXES = [
  'orders/fetchOrderHistory',
];

const orderMirrorMiddleware = (store) => (next) => (action) => {
  const type = String(action?.type || '');
  const isOrdersAction = type.startsWith('orders/');
  const prevEntities = isOrdersAction ? store.getState().orders?.entities : null;

  const result = next(action);

  if (!isOrdersAction) return result;
  if (SKIP_PREFIXES.some((p) => type.startsWith(p))) return result;

  try {
    const state = store.getState();
    const nextEntities = state.orders?.entities;
    if (!nextEntities || prevEntities === nextEntities) return result;

    // Sync id remap: handle explicitly so the local-keyed mirror row is
    // guaranteed to be replaced even though the entity's key changed.
    if (type === 'orders/remapOrderIds') {
      orderMirror.remapIds(action.payload, nextEntities);
      return result;
    }

    // Real deletion (staff deleted the order, online or offline): the entity
    // leaves Redux, so the generic diff below can't see it — mirror it as
    // cancelled explicitly, or an offline restart would resurrect it as an
    // active order occupying its table.
    if (type === 'orders/deleteOrder/fulfilled') {
      const deletedId = String(action.payload || '');
      const prevEnt = prevEntities?.[deletedId];
      if (prevEnt) {
        orderMirror.mirrorOrders([{ ...prevEnt, status: 'cancelled' }]);
        console.warn('[mirror] order marked cancelled after delete', { orderId: deletedId });
      }
      return result;
    }

    // Generic diff: entities whose reference changed were added or updated.
    const changed = [];
    for (const id of Object.keys(nextEntities)) {
      const ent = nextEntities[id];
      if (ent && (!prevEntities || prevEntities[id] !== ent)) changed.push(ent);
    }
    if (changed.length > 0) {
      // Fire-and-forget — mirroring must never block or fail a dispatch.
      orderMirror.mirrorOrders(changed);
    }
  } catch (err) {
    console.warn('[mirror] middleware error (non-fatal)', { type, errMsg: String(err?.message || err) });
  }

  return result;
};

export default orderMirrorMiddleware;
